import { OrderExaminationStatus } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { useEditOrderExaminationMutation } from "Order/Modules/Examination/Api/Mutation/useEditOrderExaminationMutation";
import {
	type EditOrderExaminationFormModel,
	getEditOrderExaminationFormSchema,
} from "Order/Modules/Examination/Components/Form/Schema/editExaminationFormSchema";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Dialog, Stack } from "@mui/material";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { type FC, useEffect } from "react";
import { useForm } from "react-hook-form";

const { form: formResource, shared: sharedResource } =
	Resources.base.examination;

type EditExaminationModalProps = {
	isOpen: boolean;
	setIsOpen: (value: boolean) => void;
	examinationId: number;
	result?: string | null;
};

export const EditExaminationModal: FC<EditExaminationModalProps> = ({
	isOpen,
	setIsOpen,
	examinationId,
	result,
}) => {
	const { t } = useResource();

	const formDefaultValues = {
		status: OrderExaminationStatus.done,
		result: result ?? "",
	};

	const form = useForm<EditOrderExaminationFormModel>({
		resolver: zodResolver(getEditOrderExaminationFormSchema()),
		defaultValues: formDefaultValues,
	});

	const {
		control,
		formState: { errors },
	} = form;

	const { mutate: editExamination, isPending } =
		useEditOrderExaminationMutation(examinationId);
	const { handleServerError } = useHandleServerError(form);

	useEffect(() => {
		form.reset(formDefaultValues);
	}, [result]);

	const handleMarkAsDone = () => {
		handleServerError(editExamination, {
			onSuccess: () => {
				setIsOpen(false);
			},
		});
	};

	return (
		<Dialog
			open={isOpen}
			onClose={() => setIsOpen(false)}
			maxWidth="sm"
			fullWidth
		>
			<DialogTitle>{t(sharedResource.checkExamination)}</DialogTitle>
			<DialogContent>
				<Box mb={2}>
					<Input
						id="result"
						name="result"
						label={t(formResource.result)}
						variant="outlined"
						control={control}
						errors={errors}
						multiline
						rows={4}
						fullWidth
					/>
				</Box>
				<Stack direction="row" spacing={1} justifyContent="flex-end">
					<Button variant="outlined" onClick={() => setIsOpen(false)}>
						{t(formResource.cancel)}
					</Button>
					<Button
						variant="contained"
						onClick={handleMarkAsDone}
						loading={isPending}
					>
						{t(formResource.done)}
					</Button>
				</Stack>
			</DialogContent>
		</Dialog>
	);
};
